'use client';

import { useEffect, useId } from 'react';
import { attributesToProps } from 'html-react-parser';
import { useLightboxStore } from '@/lib/stores/lightbox';
import { Images } from 'lucide-react';
import dynamic from 'next/dynamic';

const ShareBar = dynamic(() => import("@/components/ShareBar/ShareBar").then((mod) => mod.default), { ssr: false });

export default function ProductGalleryPreview({ images }) {
  const groupKey = useId();
  const setImages = useLightboxStore((state) => state.setImages);
  const removeImages = useLightboxStore((state) => state.removeImages);

  useEffect(() => {
    setImages(groupKey, images);
    return () => removeImages(groupKey);
  }, [groupKey, images, setImages, removeImages]);

  if (!images?.length) return null;

  const firstImage = images[0];
  const totalCount = images.length;

  // Convert raw HTML attributes to React props, then override className
  const imgProps = firstImage.attribs
    ? attributesToProps(firstImage.attribs)
    : { src: firstImage.src, alt: firstImage.alt || '' };
  imgProps.className = `${firstImage.attribs?.class || ''} cursor-zoom-in transition-transform duration-300 ease-out`.trim();

  return (
    <figure className="wp-block-gallery mom-product-gallery">
      <figure className="wp-block-image">
        <span
          className="group mx-auto block relative cursor-pointer overflow-hidden"
          onClick={(e) => {
            if (e.target.closest('.mom-share-bar')) return;
            useLightboxStore.getState().openBySource(firstImage.src);
          }}
        >
          <img {...imgProps} />
          {totalCount > 1 ? (
            <span className={
              'absolute top-2 right-2 h-[34px] px-2.5 rounded-full bg-black/50 text-white ' +
              'flex items-center justify-center gap-1.5 ' +
              'pointer-events-none'
            }>
              <Images size={16} strokeWidth={2} />
              <span className="text-xs font-semibold">1/{totalCount}</span>
            </span>
          ) : (
            <span className={
              'absolute top-2 right-2 size-[34px] rounded-full bg-black/50 text-white ' +
              'flex items-center justify-center ' +
              'pointer-events-none'
            }>
              <Images size={16} strokeWidth={2} />
            </span>
          )}
          <ShareBar
            className="mom-share-bar"
            url={firstImage.src}
            variant="alter"
            facebook x pinterest copy
          />
        </span>
        {firstImage.caption && (
          <figcaption className="wp-element-caption">{firstImage.caption}</figcaption>
        )}
      </figure>
    </figure>
  );
}
